import { useState } from "react";
import {
  InfiniteListBase,
  RecordRepresentation,
  ShowBase,
  useShowContext,
  useTranslate,
} from "ra-core";
import type { ShowBaseProps } from "ra-core";
import { useIsMobile } from "@/hooks/use-mobile";
import { ReferenceField } from "@/components/admin/reference-field";
import { TextField } from "@/components/admin/text-field";
import { Card, CardContent } from "@/components/ui/card";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import { Button } from "@/components/ui/button";
import { Separator } from "@/components/ui/separator";
import { Pencil } from "lucide-react";
import { Link } from "react-router";

import MobileHeader from "../layout/MobileHeader";
import { MobileContent } from "../layout/MobileContent";
import { RecordHeader } from "../../gravun/record/RecordHeader";
import { CompanyAvatar } from "../companies/CompanyAvatar";
import { NoteCreate, NotesIterator, NotesIteratorMobile } from "../notes";
import { NoteCreateSheet } from "../notes/NoteCreateSheet";
import { TagsListEdit } from "./TagsListEdit";
import { ContactEditSheet } from "./ContactEditSheet";
import { ContactStatusSelector } from "./ContactInputs";
import { ContactPersonalInfo } from "./ContactPersonalInfo";
import { ContactBackgroundInfo } from "./ContactBackgroundInfo";
import { ContactTasksList } from "./ContactTasksList";
import type { Contact } from "../types";
import { Avatar } from "./Avatar";
import { ContactAside } from "./ContactAside";
import { MobileBackButton } from "../misc/MobileBackButton";
import { RecordActionBar } from "../../gravun/comms/RecordActionBar";
import { ContactTimeline } from "../../gravun/comms/ContactTimeline";
import { CommentThread } from "../../gravun/comments/CommentThread";
import { RecordFiles } from "../../gravun/files/RecordFiles";
import { DuplicateWarning } from "../../gravun/duplicates/DuplicateWarning";
import { ContactCampaignHistory } from "../../gravun/campaigns/ContactCampaignHistory";

export const ContactShow = (props: ShowBaseProps = {}) => {
  const isMobile = useIsMobile();

  return (
    <ShowBase
      queryOptions={{
        onError: isMobile
          ? () => {
              {
                /** Disable error notification as the content handles offline */
              }
            }
          : undefined,
      }}
      {...props}
    >
      {isMobile ? <ContactShowContentMobile /> : <ContactShowContent />}
    </ShowBase>
  );
};

const ContactShowContentMobile = () => {
  const translate = useTranslate();
  const { defaultTitle, record, isPending } = useShowContext<Contact>();
  const [noteCreateOpen, setNoteCreateOpen] = useState(false);
  const [editOpen, setEditOpen] = useState(false);
  if (isPending || !record) return null;

  const taskCount = record.nb_tasks ?? 0;

  return (
    <>
      {/* We need to repeat the note creation sheet here to support the note 
      create button that is rendered when there are no notes. */}
      <NoteCreateSheet
        open={noteCreateOpen}
        onOpenChange={setNoteCreateOpen}
        contact_id={record.id}
      />
      <ContactEditSheet
        open={editOpen}
        onOpenChange={setEditOpen}
        contactId={record.id}
      />
      <MobileHeader>
        <MobileBackButton />
        <div className="flex flex-1 min-w-0">
          <Link to="/contacts" className="flex-1 min-w-0">
            <h1 className="truncate text-xl font-semibold">{defaultTitle}</h1>
          </Link>
        </div>
        <Button
          type="button"
          variant="ghost"
          size="icon"
          className="rounded-full"
          aria-label={translate("ra.action.edit")}
          onClick={() => setEditOpen(true)}
        >
          <Pencil className="size-5" />
        </Button>
      </MobileHeader>
      <MobileContent>
        <div className="mb-6">
          <div className="flex items-center mb-4">
            <Avatar />
            <div className="mx-3 flex-1">
              <h2 className="text-2xl font-bold">
                <RecordRepresentation />
              </h2>
              <div className="text-sm text-muted-foreground">
                {record.title && record.company_id != null
                  ? `${translate("resources.contacts.position_at", {
                      title: record.title,
                    })} `
                  : record.title}
                {record.company_id != null && (
                  <ReferenceField
                    source="company_id"
                    reference="companies"
                    link="show"
                  >
                    <TextField source="name" className="underline" />
                  </ReferenceField>
                )}
              </div>
            </div>
            <div>
              <ReferenceField
                source="company_id"
                reference="companies"
                link="show"
                className="no-underline"
              >
                <CompanyAvatar />
              </ReferenceField>
            </div>
          </div>
        </div>

        {/* Mobile: the six customer actions come first, this is the screen a
            technician uses in a driveway. */}
        <div className="mb-3">
          <DuplicateWarning contactId={Number(record.id)} />
          <RecordActionBar contact={record} />
        </div>

        <Tabs defaultValue="timeline" className="w-full">
          <TabsList className="grid h-10 w-full grid-cols-4">
            <TabsTrigger value="timeline">Timeline</TabsTrigger>
            <TabsTrigger value="notes">
              {translate("resources.notes.name", { smart_count: 2 })}
            </TabsTrigger>
            <TabsTrigger value="tasks">
              {translate("crm.common.task_count", {
                smart_count: taskCount ?? 0,
              })}
            </TabsTrigger>
            <TabsTrigger value="comments">Comments</TabsTrigger>
            <TabsTrigger value="files">Files</TabsTrigger>
            <TabsTrigger value="details">
              {translate("crm.common.details")}
            </TabsTrigger>
          </TabsList>

          <TabsContent value="timeline" className="mt-3">
            <ContactTimeline contactId={record.id} />
          </TabsContent>

          <TabsContent value="comments" className="mt-3">
            <CommentThread subject={{ contact_id: Number(record.id) }} />
          </TabsContent>

          {/* Files get their own tab rather than living under "Comments":
              on a phone nobody hunting for a site photo thinks to look
              inside a discussion thread. */}
          <TabsContent value="files" className="mt-3">
            <RecordFiles subject={{ contact_id: Number(record.id) }} />
            <div className="mt-6">
              <ContactCampaignHistory contactId={Number(record.id)} />
            </div>
          </TabsContent>

          <TabsContent value="notes" className="mt-2">
            <InfiniteListBase
              resource="contact_notes"
              filter={{ contact_id: record.id }}
              sort={{ field: "date", order: "DESC" }}
              perPage={25}
              disableSyncWithLocation
              storeKey={false}
              empty={
                <div className="flex flex-col items-center justify-center py-8 text-center">
                  <p className="text-muted-foreground mb-4">
                    {translate("resources.notes.empty")}
                  </p>
                  <Button
                    variant="outline"
                    onClick={() => setNoteCreateOpen(true)}
                  >
                    {translate("resources.notes.action.add")}
                  </Button>
                </div>
              }
              loading={false}
              error={false}
              queryOptions={{
                onError: () => {
                  /** override to hide notification as error case is handled by NotesIteratorMobile */
                },
              }}
            >
              <NotesIteratorMobile contactId={record.id} showStatus />
            </InfiniteListBase>
          </TabsContent>

          <TabsContent value="tasks" className="mt-4">
            <ContactTasksList />
          </TabsContent>

          <TabsContent value="details" className="mt-4">
            <div className="space-y-6">
              <div>
                <h3 className="text-lg font-semibold">
                  {translate("resources.notes.fields.status")}
                </h3>
                <Separator />
                <div className="mt-3">
                  <ContactStatusSelector />
                </div>
              </div>
              <div>
                <h3 className="text-lg font-semibold">
                  {translate(
                    "resources.contacts.field_categories.personal_info",
                  )}
                </h3>
                <Separator />
                <div className="mt-3">
                  <ContactPersonalInfo />
                </div>
              </div>
              <div>
                <h3 className="text-lg font-semibold">
                  {translate(
                    "resources.contacts.field_categories.background_info",
                  )}
                </h3>
                <Separator />
                <div className="mt-3">
                  <ContactBackgroundInfo />
                </div>
              </div>
              <div>
                <h3 className="text-lg font-semibold">
                  {translate("resources.tags.name", { smart_count: 2 })}
                </h3>
                <Separator />
                <div className="mt-3">
                  <TagsListEdit />
                </div>
              </div>
            </div>
          </TabsContent>
        </Tabs>
      </MobileContent>
    </>
  );
};

const ContactShowContent = () => {
  const translate = useTranslate();
  const { record, isPending } = useShowContext<Contact>();
  if (isPending || !record) return null;

  return (
    <div>
      <RecordHeader
        backTo="/contacts"
        backLabel="Contacts"
        avatar={<Avatar width={40} height={40} />}
        title={<RecordRepresentation />}
        subtitle={
          <span className="inline-flex items-center gap-1">
            {record.title && record.company_id != null
              ? `${translate("resources.contacts.position_at", {
                  title: record.title,
                })} `
              : record.title}
            {record.company_id != null && (
              <ReferenceField
                source="company_id"
                reference="companies"
                link="show"
              >
                <TextField source="name" className="hover:underline" />
              </ReferenceField>
            )}
          </span>
        }
        actions={
          <ReferenceField
            source="company_id"
            reference="companies"
            link="show"
            className="no-underline"
          >
            <CompanyAvatar />
          </ReferenceField>
        }
      />
      <div className="mt-1 mb-4">
        <DuplicateWarning contactId={Number(record.id)} />
        <RecordActionBar contact={record} />
      </div>
      <div className="flex flex-col gap-6 lg:flex-row">
        <div className="min-w-0 flex-1">
          <Card className="mb-4 py-4">
            <CardContent className="px-5">
              <h2 className="mb-2 text-[10.5px] font-semibold uppercase tracking-[0.09em] text-muted-foreground">
                Timeline
              </h2>
              <ContactTimeline contactId={record.id} />
            </CardContent>
          </Card>
          <Card className="mb-4 py-4">
            <CardContent className="px-5">
              <CommentThread subject={{ contact_id: Number(record.id) }} />
            </CardContent>
          </Card>
          <Card className="mb-4 py-4">
            <CardContent className="px-5">
              <RecordFiles subject={{ contact_id: Number(record.id) }} />
            </CardContent>
          </Card>
          <Card className="mb-4 py-4">
            <CardContent className="px-5">
              <ContactCampaignHistory contactId={Number(record.id)} />
            </CardContent>
          </Card>
          <Card className="py-4">
            <CardContent className="px-5">
              <h2 className="mb-1 text-[10.5px] font-semibold uppercase tracking-[0.09em] text-muted-foreground">
                {translate("crm.dashboard.latest_notes", { _: "Activity" })}
              </h2>
              <InfiniteListBase
                resource="contact_notes"
                filter={{ contact_id: record.id }}
                sort={{ field: "date", order: "DESC" }}
                perPage={25}
                disableSyncWithLocation
                storeKey={false}
                empty={
                  <NoteCreate
                    reference="contacts"
                    showStatus
                    className="mt-4"
                  />
                }
              >
                <NotesIterator reference="contacts" showStatus />
              </InfiniteListBase>
            </CardContent>
          </Card>
        </div>
        <div className="w-full shrink-0 lg:w-80">
          <Card className="py-4">
            <CardContent className="px-5">
              <ContactAside />
            </CardContent>
          </Card>
        </div>
      </div>
    </div>
  );
};
